import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Sparkles, RefreshCw, TrendingUp, Building2, Users, BarChart3, MessageSquare, Gem, ShieldAlert } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function EarlyAlpha({ onAnalyze }) {
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [minScore, setMinScore] = useState(20);
  const [maxMcap, setMaxMcap] = useState(20000);
  const [includeMega, setIncludeMega] = useState(false);
  const [scannedAt, setScannedAt] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const fetchPicks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        min_score: minScore,
        limit: 25,
        min_mcap_m: 300,
        max_mcap_m: maxMcap,
        include_mega: includeMega,
      });
      const r = await axios.get(`${API}/api/early-alpha/scan?${params}`);
      setPicks(r.data.picks || []);
      setScannedAt(r.data.scanned_at);
    } catch (e) {
      console.error("early-alpha scan failed", e);
    } finally {
      setLoading(false);
    }
  }, [minScore, maxMcap, includeMega]);

  useEffect(() => { fetchPicks(); }, [fetchPicks]);

  const scoreColor = (s) => {
    if (s >= 60) return "from-emerald-500 to-teal-400 text-emerald-950";
    if (s >= 40) return "from-amber-500 to-yellow-400 text-amber-950";
    if (s >= 25) return "from-sky-500 to-blue-400 text-sky-950";
    return "from-slate-600 to-slate-500 text-slate-100";
  };

  const ScoreBadge = ({ score }) => (
    <div
      data-testid="alpha-score-badge"
      className={`text-2xl font-black bg-gradient-to-br ${scoreColor(score)} rounded-xl px-4 py-2 shadow-lg min-w-[68px] text-center`}
    >
      {score}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="early-alpha-tab">
      {/* HEADER */}
      <div className="bg-gradient-to-br from-fuchsia-900/40 via-violet-900/30 to-slate-900/60 border border-fuchsia-700/40 rounded-2xl p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-3 mb-2">
              <Sparkles className="w-7 h-7 text-fuchsia-400" />
              EARLY ALPHA — Avant Wall Street
            </h2>
            <p className="text-sm text-slate-400 max-w-2xl">
              Croise 7 signaux pré-retail (insider buys, 13F smart money, volume anormal,
              buzz Reddit pré-viral, gems fondamentaux, faible couverture analystes).
              Filtre par défaut : market cap entre $300M et $20B — la zone où l'alpha vit.
            </p>
          </div>
          <button
            data-testid="early-alpha-refresh-btn"
            onClick={fetchPicks}
            disabled={loading}
            className="bg-fuchsia-600 hover:bg-fuchsia-500 disabled:opacity-50 text-white font-semibold px-4 py-2 rounded-lg flex items-center gap-2 shrink-0"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Scanner
          </button>
        </div>

        {/* FILTERS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Score minimum (0-100)</label>
            <input
              data-testid="early-alpha-min-score"
              type="range"
              min={10}
              max={80}
              step={5}
              value={minScore}
              onChange={(e) => setMinScore(parseInt(e.target.value))}
              className="w-full accent-fuchsia-500"
            />
            <div className="text-xs text-fuchsia-300 mt-1">≥ {minScore}</div>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Cap max ($M)</label>
            <select
              data-testid="early-alpha-max-mcap"
              value={maxMcap}
              onChange={(e) => setMaxMcap(parseInt(e.target.value))}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm"
            >
              <option value={2000}>≤ $2B (micro/small caps)</option>
              <option value={10000}>≤ $10B (small/mid)</option>
              <option value={20000}>≤ $20B (sweet spot)</option>
              <option value={100000}>≤ $100B (all but mega)</option>
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                data-testid="early-alpha-include-mega"
                type="checkbox"
                checked={includeMega}
                onChange={(e) => setIncludeMega(e.target.checked)}
                className="w-4 h-4 accent-fuchsia-500"
              />
              Inclure les mega-caps (NVDA, AAPL, ...)
            </label>
          </div>
        </div>

        {scannedAt && (
          <div className="text-xs text-slate-500 mt-3">
            Dernier scan : {new Date(scannedAt).toLocaleString("fr-FR")} · {picks.length} pépite{picks.length > 1 ? "s" : ""} détectée{picks.length > 1 ? "s" : ""}
          </div>
        )}
      </div>

      {/* PICKS LIST */}
      {loading && picks.length === 0 && (
        <div className="text-center text-slate-500 py-12">
          <RefreshCw className="w-8 h-8 mx-auto animate-spin mb-3 text-fuchsia-400" />
          Croisement des 7 signaux en cours...
        </div>
      )}

      {!loading && picks.length === 0 && (
        <div className="text-center text-slate-500 py-12 border border-slate-800 rounded-xl">
          <ShieldAlert className="w-10 h-10 mx-auto mb-3 text-slate-600" />
          Aucune pépite ne franchit le seuil de {minScore} points. Baissez le seuil ou élargissez la fourchette de cap.
        </div>
      )}

      {picks.map((p) => {
        const isExpanded = expanded === p.ticker;
        return (
          <div
            key={p.ticker}
            data-testid={`early-alpha-pick-${p.ticker}`}
            className="bg-slate-900/70 border border-slate-800 hover:border-fuchsia-700/60 rounded-xl p-5 transition-all"
          >
            <div className="flex items-start gap-4">
              <ScoreBadge score={p.alpha_score} />

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    data-testid={`early-alpha-analyze-${p.ticker}`}
                    onClick={() => onAnalyze && onAnalyze(p.ticker)}
                    className="text-xl font-bold text-white hover:text-fuchsia-300 transition"
                  >
                    {p.ticker}
                  </button>
                  <span className="text-sm text-slate-400 truncate max-w-md">{p.name}</span>
                  {p.market_cap_m && (
                    <span className="text-xs bg-slate-800 text-slate-400 px-2 py-0.5 rounded-full">
                      ${(p.market_cap_m / 1000).toFixed(1)}B
                    </span>
                  )}
                  {p.sector && (
                    <span className="text-xs bg-violet-900/50 text-violet-300 px-2 py-0.5 rounded-full">
                      {p.sector}
                    </span>
                  )}
                  <span className="text-xs bg-fuchsia-900/50 text-fuchsia-300 px-2 py-0.5 rounded-full">
                    {p.n_signals} signaux convergents
                  </span>
                </div>

                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <div className="text-slate-500 text-xs">Prix</div>
                    <div className="font-semibold">${(p.price || 0).toFixed(2)}</div>
                  </div>
                  {p.trade_setup.entry > 0 && (
                    <div>
                      <div className="text-slate-500 text-xs">Entrée</div>
                      <div className="font-semibold text-emerald-300">${p.trade_setup.entry.toFixed(2)}</div>
                    </div>
                  )}
                  {p.trade_setup.target > 0 && (
                    <div>
                      <div className="text-slate-500 text-xs">Target</div>
                      <div className="font-semibold text-sky-300">${p.trade_setup.target.toFixed(2)}</div>
                    </div>
                  )}
                  {p.trade_setup.risk_reward > 0 && (
                    <div>
                      <div className="text-slate-500 text-xs">R:R</div>
                      <div className="font-semibold text-amber-300">{p.trade_setup.risk_reward.toFixed(1)}</div>
                    </div>
                  )}
                </div>

                <ul className="mt-3 space-y-1 text-sm">
                  {p.reasons.map((r, i) => (
                    <li key={i} className="text-slate-300">{r}</li>
                  ))}
                </ul>

                <button
                  data-testid={`early-alpha-toggle-${p.ticker}`}
                  onClick={() => setExpanded(isExpanded ? null : p.ticker)}
                  className="mt-3 text-xs text-fuchsia-400 hover:text-fuchsia-300"
                >
                  {isExpanded ? "▲ Cacher détails" : "▼ Voir détails signaux"}
                </button>

                {isExpanded && (
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                    {p.signals.insider && (
                      <div className="bg-emerald-900/20 border border-emerald-800/40 rounded-lg p-3">
                        <div className="flex items-center gap-2 font-semibold text-emerald-300 mb-1">
                          <Users className="w-3.5 h-3.5" /> Insider
                        </div>
                        <div className="text-slate-300">{p.signals.insider.name}</div>
                        <div className="text-slate-400">
                          ${(p.signals.insider.value / 1e6).toFixed(2)}M · {p.signals.insider.strength} · {p.signals.insider.date}
                        </div>
                      </div>
                    )}
                    {p.signals.smart_money && (
                      <div className="bg-sky-900/20 border border-sky-800/40 rounded-lg p-3">
                        <div className="flex items-center gap-2 font-semibold text-sky-300 mb-1">
                          <Building2 className="w-3.5 h-3.5" /> Smart Money 13F
                        </div>
                        <div className="text-slate-300">{p.signals.smart_money.fund}</div>
                        <div className="text-slate-400">
                          ${p.signals.smart_money.value_millions?.toFixed(0)}M · {p.signals.smart_money.weight_pct?.toFixed(1)}% du PF
                        </div>
                      </div>
                    )}
                    {p.signals.gem && p.signals.gem.grade && (
                      <div className="bg-violet-900/20 border border-violet-800/40 rounded-lg p-3">
                        <div className="flex items-center gap-2 font-semibold text-violet-300 mb-1">
                          <Gem className="w-3.5 h-3.5" /> Fondamentaux
                        </div>
                        <div className="text-slate-300">Grade {p.signals.gem.grade} · Score {p.signals.gem.total_score}</div>
                        <div className="text-slate-400">
                          {p.signals.gem.revenue_growth_pct ? `Rev +${p.signals.gem.revenue_growth_pct.toFixed(0)}%` : ""}
                          {p.signals.gem.insider_net ? ` · Insider net ${p.signals.gem.insider_net > 0 ? "+" : ""}${p.signals.gem.insider_net}` : ""}
                          {p.signals.gem.forward_pe ? ` · Fwd PE ${p.signals.gem.forward_pe.toFixed(1)}` : ""}
                        </div>
                      </div>
                    )}
                    {p.signals.scan && p.signals.scan.composite_score && (
                      <div className="bg-amber-900/20 border border-amber-800/40 rounded-lg p-3">
                        <div className="flex items-center gap-2 font-semibold text-amber-300 mb-1">
                          <BarChart3 className="w-3.5 h-3.5" /> Scanner
                        </div>
                        <div className="text-slate-300">{p.signals.scan.action} · Score {p.signals.scan.composite_score}</div>
                        <div className="text-slate-400">
                          RSI {p.signals.scan.rsi?.toFixed(0)} · Vol {p.signals.scan.vol_ratio_5d?.toFixed(1)}x · 1m {p.signals.scan.change_1m?.toFixed(1)}%
                        </div>
                      </div>
                    )}
                    {p.signals.social && (
                      <div className="bg-pink-900/20 border border-pink-800/40 rounded-lg p-3">
                        <div className="flex items-center gap-2 font-semibold text-pink-300 mb-1">
                          <MessageSquare className="w-3.5 h-3.5" /> Reddit / Social
                        </div>
                        <div className="text-slate-300">{p.signals.social.mentions} mentions · buzz {p.signals.social.buzz_score?.toFixed(0)}/100</div>
                        <div className="text-slate-400">{(p.signals.social.subreddits || []).join(", ")}</div>
                      </div>
                    )}
                    {p.signals.fund_extras && (p.signals.fund_extras.n_analysts || p.signals.fund_extras.short_pct) && (
                      <div className="bg-rose-900/20 border border-rose-800/40 rounded-lg p-3">
                        <div className="flex items-center gap-2 font-semibold text-rose-300 mb-1">
                          <TrendingUp className="w-3.5 h-3.5" /> Couverture
                        </div>
                        {p.signals.fund_extras.n_analysts !== null && p.signals.fund_extras.n_analysts !== undefined && (
                          <div className="text-slate-300">{p.signals.fund_extras.n_analysts} analystes</div>
                        )}
                        {p.signals.fund_extras.short_pct && (
                          <div className="text-slate-400">Short {p.signals.fund_extras.short_pct.toFixed(1)}%</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
